import * as Sentry from "@sentry/react";
import { API_BASE_URL, SENTRY_DSN, SENTRY_ENVIRONMENT } from "constants/env";
import {
  CaptureContext,
  Intrumentation,
  SeverityLevel,
  Span,
  StartSpanOptions,
  User,
} from "types/instrumentation";

const sentry: Intrumentation = {
  init() {
    Sentry.init({
      dsn: SENTRY_DSN,
      sendDefaultPii: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: 0.25,
      tracePropagationTargets: [API_BASE_URL],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      strictTraceContinuation: true,
      environment: SENTRY_ENVIRONMENT,
    });
  },

  startActiveSpan<R>(
    name: string,
    callback: (span: Span) => R,
    options?: StartSpanOptions
  ) {
    return Sentry.startSpan(
      { name, attributes: options?.attributes },
      callback
    );
  },

  startInactiveSpan<R>(name: string, options?: StartSpanOptions) {
    return Sentry.startInactiveSpan({ name, attributes: options?.attributes });
  },

  startSpanManual<R>(
    name: string,
    callback: (span: Span) => R,
    options?: StartSpanOptions
  ) {
    return Sentry.startSpan(
      { name, attributes: options?.attributes },
      callback
    );
  },

  getActiveSpan(): Span | undefined {
    return Sentry.getActiveSpan();
  },

  getRootSpan(span: Span): Span {
    return Sentry.getRootSpan(span);
  },

  captureException(exception: unknown, captureContext?: CaptureContext) {
    return Sentry.captureException(exception, captureContext);
  },

  captureMessage(
    message: string,
    captureContext?: CaptureContext | SeverityLevel
  ) {
    return Sentry.captureMessage(message, captureContext);
  },

  captureReactException(error: Error, info: { componentStack: string }) {
    return Sentry.captureReactException(error, info);
  },

  setUser(user: User | null) {
    return Sentry.setUser(user);
  },

  setTag(key: string, value: string) {
    Sentry.setTag(key, value);
  },

  setTags(tags: Record<string, string>) {
    return Sentry.setTags(tags);
  },

  setExtra(name: string, extra: unknown) {
    return Sentry.setExtra(name, extra);
  },

  setExtras(extras: Record<string, unknown>) {
    return Sentry.setExtras(extras);
  },
};

export default sentry;
